import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ChevronDown, Check, Copy, ExternalLink, X } from "lucide-react";
import { ethers } from "ethers";
import AmountPercentSlider from "./AmountPercentSlider";
import TokenIcon from "../TokenIcon";
import { truncateDecimalValue } from "@/lib/utils";
import { useZNSResolver, isZNSDomain } from "@/hooks/useZNSResolver";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

export interface SendTokenOption {
  symbol: string;
  name: string;
  icon: string;
  balance: string;
  decimals: number;
  contractAddress?: string;
}

export interface SendTransferParams {
  symbol: string;
  recipient: string;
  amount: string;
}

interface SendModalProps {
  isOpen: boolean;
  /** When false, the modal will not render even if isOpen is true. */
  isPrivateUnlocked?: boolean;
  onClose: () => void;
  privateTokens?: SendTokenOption[];
  walletAddress?: string;
  explorerBaseUrl?: string;
  onSend?: (params: SendTransferParams) => Promise<{ txHash: string }>;
}

type SendPhase = "input" | "review" | "sending" | "success" | "error";

const MAX_AMOUNT_DECIMALS = 10;
const AMOUNT_INPUT_REGEX = new RegExp(`^\\d*\\.?\\d{0,${MAX_AMOUNT_DECIMALS}}$`);
const TX_HASH_PATTERN = /\b(0x[a-fA-F0-9]{64})\b/;

function extractTxHashFromError(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const e = err as {
    txHash?: string;
    hash?: string;
    receipt?: { hash?: string };
    transaction?: { hash?: string };
    message?: string;
  };
  const direct = e.txHash || e.hash || e.receipt?.hash || e.transaction?.hash;
  if (direct) return direct;

  const msg = e.message ?? "";
  const labeled = msg.match(/\bTX:\s*(0x[a-fA-F0-9]{64})\b/);
  if (labeled) return labeled[1];

  const embedded = msg.match(TX_HASH_PATTERN);
  return embedded ? embedded[1] : null;
}

const SendModal: React.FC<SendModalProps> = ({
  isOpen,
  isPrivateUnlocked = true,
  onClose,
  privateTokens = [],
  walletAddress,
  explorerBaseUrl = "https://testnet.cotiscan.io",
  onSend,
}) => {
  const [selectedToken, setSelectedToken] = useState<SendTokenOption | null>(null);
  const [showTokenDropdown, setShowTokenDropdown] = useState(false);
  const [recipientAddress, setRecipientAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [sliderValue, setSliderValue] = useState(0);
  const [phase, setPhase] = useState<SendPhase>("input");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [copiedAddress, setCopiedAddress] = useState(false);

  const {
    address: resolvedAddress,
    isResolving,
    error: znsError,
    resolveAddress,
    clearResolver,
  } = useZNSResolver();

  const trimmedRecipient = recipientAddress.trim();
  const isDomainRecipient = isZNSDomain(trimmedRecipient);
  // The address the transfer actually goes to: the ZNS owner for .coti
  // domains, the raw input otherwise.
  const effectiveRecipient = isDomainRecipient ? resolvedAddress ?? "" : trimmedRecipient;

  const visible = isOpen && isPrivateUnlocked;

  useBodyScrollLock(visible);

  useEffect(() => {
    if (!visible) {
      setPhase("input");
      setRecipientAddress("");
      setAmount("");
      setSliderValue(0);
      setErrorMessage(null);
      setTxHash(null);
      setShowTokenDropdown(false);
    }
  }, [visible]);

  useEffect(() => {
    if (!isDomainRecipient) {
      clearResolver();
      return;
    }
    const timer = setTimeout(() => {
      resolveAddress(trimmedRecipient);
    }, 400);
    return () => clearTimeout(timer);
  }, [trimmedRecipient, isDomainRecipient, resolveAddress, clearResolver]);

  useEffect(() => {
    if (privateTokens.length > 0) {
      if (!selectedToken || !privateTokens.find(t => t.symbol === selectedToken.symbol)) {
        setSelectedToken(privateTokens[0]);
      } else {
        const updated = privateTokens.find(t => t.symbol === selectedToken.symbol);
        if (updated && updated.balance !== selectedToken.balance) {
          setSelectedToken(updated);
        }
      }
    } else {
      setSelectedToken(null);
    }
  }, [privateTokens, selectedToken]);

  const displayDecimals = Math.min(selectedToken?.decimals ?? 18, MAX_AMOUNT_DECIMALS);
  const maxBalance = selectedToken ? parseFloat(selectedToken.balance.replace(/,/g, "")) : 0;
  const displaySymbol = selectedToken ? selectedToken.symbol : "";

  const formatAmountForToken = (value: number | string) =>
    truncateDecimalValue(value, displayDecimals);

  const handleSliderChange = (value: number[]) => {
    const pct = value[0];
    setSliderValue(pct);
    const computed = (maxBalance * pct) / 100;
    setAmount(computed > 0 ? formatAmountForToken(computed) : "");
  };

  const handleAmountChange = (val: string) => {
    setAmount(val);
    const num = parseFloat(val);
    if (!isNaN(num) && maxBalance > 0) {
      setSliderValue(Math.min(100, Math.round((num / maxBalance) * 100)));
    } else {
      setSliderValue(0);
    }
  };

  const handleMax = () => {
    if (!selectedToken) return;
    setAmount(formatAmountForToken(selectedToken.balance.replace(/,/g, "")));
    setSliderValue(100);
  };

  const handleCopyAddress = () => {
    if (!effectiveRecipient) return;
    navigator.clipboard.writeText(effectiveRecipient).then(() => {
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    });
  };

  const handleSelectToken = (token: SendTokenOption) => {
    setSelectedToken(token);
    setShowTokenDropdown(false);
    setAmount("");
    setSliderValue(0);
  };

  const validateInput = (): string | null => {
    if (!selectedToken) return "Select a token to send.";
    if (!selectedToken.contractAddress) return "This token is not available for send on this network.";
    if (!trimmedRecipient) return "Enter a recipient address.";
    if (isDomainRecipient) {
      if (isResolving) return "Resolving .coti domain…";
      if (znsError) return znsError;
      if (!resolvedAddress) return "Could not resolve this domain.";
    } else if (!ethers.isAddress(trimmedRecipient)) {
      return "Enter a valid wallet address.";
    }
    if (
      walletAddress &&
      effectiveRecipient.toLowerCase() === walletAddress.toLowerCase()
    ) {
      return "Cannot send to your own address.";
    }
    if (!amount || !Number.isFinite(Number(amount)) || Number(amount) <= 0) {
      return "Enter an amount greater than zero.";
    }
    if (Number(amount) > maxBalance) {
      return "Amount exceeds available balance.";
    }
    return null;
  };

  const handleReviewTransfer = () => {
    setErrorMessage(null);
    const validationError = validateInput();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    setPhase("review");
  };

  const handleConfirmSend = async () => {
    if (!selectedToken || !onSend) return;

    setErrorMessage(null);
    setTxHash(null);
    setPhase("sending");

    try {
      const result = await onSend({
        symbol: selectedToken.symbol,
        recipient: effectiveRecipient,
        amount,
      });
      setTxHash(result.txHash);
      setPhase("success");
    } catch (err: unknown) {
      let message = "Transfer failed";
      if (err && typeof err === "object") {
        const e = err as { code?: number; message?: string };
        if (e.code === 4001 || e.message?.includes("rejected")) {
          message = "Transaction rejected in wallet.";
        } else if (e.message) {
          message = e.message;
        }
      }
      setErrorMessage(message);
      setTxHash(extractTxHashFromError(err));
      setPhase("error");
    }
  };

  const handleClose = () => {
    if (phase === "sending") return;
    onClose();
  };

  const truncateAddress = (address: string) =>
    address.length > 12 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address;

  const formatBalanceMinDecimals = (balance: string, minDecimals = 2) => {
    const [integerPart, decimalPart = ""] = balance.split(".");
    return `${integerPart}.${decimalPart.padEnd(minDecimals, "0")}`;
  };

  const renderInputPhase = () => (
    <>
      <div className="mb-3">
        <label className="mb-1 block text-[10px] tracking-wider text-muted-foreground">Asset</label>
        {privateTokens.length === 0 ? (
          <div className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-3 text-muted-foreground text-sm">
            No private tokens available
          </div>
        ) : selectedToken ? (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowTokenDropdown(!showTokenDropdown)}
              className="flex w-full items-center justify-between gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2 text-left transition-colors hover:border-border"
            >
              <div className="flex min-w-0 items-center gap-2">
                <TokenIcon icon={selectedToken.icon} isPrivate size="sm" />
                <div className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-foreground">{selectedToken.name}</span>
                  <span className="block text-[11px] text-muted-foreground">{selectedToken.symbol}</span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <span className="hidden text-xs text-muted-foreground sm:inline">{formatBalanceMinDecimals(selectedToken.balance)}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </div>
            </button>

            <AnimatePresence>
              {showTokenDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="absolute top-full left-0 right-0 z-10 mt-1 max-h-40 overflow-y-auto rounded-lg border border-border modal-dropdown"
                >
                  {privateTokens.map((token) => (
                    <button
                      key={token.symbol}
                      type="button"
                      onClick={() => handleSelectToken(token)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left transition-colors hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-2">
                        <TokenIcon icon={token.icon} isPrivate size="sm" />
                        <div className="min-w-0">
                          <span className="block text-sm text-foreground">{token.name}</span>
                          <span className="block text-[11px] text-muted-foreground">{token.symbol}</span>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatBalanceMinDecimals(token.balance)}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : null}
      </div>

      <div className="mb-3">
        <label className="mb-1 block text-[10px] tracking-wider text-muted-foreground">To</label>
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2 focus-within:border-border">
          <input
            type="text"
            placeholder="Wallet address or ZNS domain"
            value={recipientAddress}
            onChange={(e) => setRecipientAddress(e.target.value)}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            className="min-w-0 flex-1 bg-transparent text-base sm:text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
          <button
            type="button"
            onClick={handleCopyAddress}
            aria-label="Copy address"
            className="shrink-0 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
            disabled={!recipientAddress.trim()}
          >
            {copiedAddress ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
        </div>
        {isDomainRecipient && isResolving && (
          <p className="mt-1 text-[11px] text-muted-foreground">Resolving .coti domain…</p>
        )}
        {isDomainRecipient && !isResolving && resolvedAddress && (
          <p className="mt-1 text-[11px] text-green-600 dark:text-green-400">
            Resolved to {truncateAddress(resolvedAddress)}
          </p>
        )}
        {isDomainRecipient && !isResolving && znsError && (
          <p className="mt-1 text-[11px] text-red-500 dark:text-red-400">{znsError}</p>
        )}
      </div>

      <div className="mb-3">
        <label className="mb-1 block text-[10px] tracking-wider text-muted-foreground">Amount</label>
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2 focus-within:border-border">
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={amount}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || AMOUNT_INPUT_REGEX.test(val)) handleAmountChange(val);
            }}
            className="min-w-0 flex-1 bg-transparent text-lg sm:text-base font-semibold text-foreground outline-none placeholder:text-muted-foreground"
          />
          <span className="shrink-0 text-xs font-medium text-muted-foreground">{displaySymbol}</span>
          <button
            type="button"
            onClick={handleMax}
            className="shrink-0 rounded-full border border-primary/40 px-2.5 py-0.5 text-[11px] font-medium text-primary dark:border-cyan-400/50 dark:text-cyan-400 hover:bg-primary/10 dark:hover:bg-cyan-400/10"
          >
            Max
          </button>
        </div>
        {selectedToken && (
          <p className="mt-1 text-[11px] text-muted-foreground sm:hidden">
            Available: {formatBalanceMinDecimals(selectedToken.balance)} {displaySymbol}
          </p>
        )}
      </div>

      <AmountPercentSlider
        value={sliderValue}
        onValueChange={(pct) => handleSliderChange([pct])}
        compact
      />

      {errorMessage && phase === "input" && (
        <p className="mt-4 text-sm text-red-500 dark:text-red-400">{errorMessage}</p>
      )}

      <button
        type="button"
        onClick={handleReviewTransfer}
        disabled={
          !amount ||
          !recipientAddress ||
          !selectedToken ||
          privateTokens.length === 0 ||
          !onSend ||
          (isDomainRecipient && (isResolving || !resolvedAddress))
        }
        className="mt-7 flex w-full items-center justify-center gap-2 rounded-xl border border-border modal-cta py-3 text-sm font-semibold text-foreground transition-all hover:border-border hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Review
      </button>
    </>
  );

  const renderReviewPhase = () => (
    <>
      <div className="space-y-3 rounded-xl border border-border bg-muted/30 p-4 text-sm">
        <div className="flex items-start justify-between gap-3">
          <span className="text-muted-foreground">Asset</span>
          <span className="text-right font-medium text-foreground">
            {selectedToken?.name} ({selectedToken?.symbol})
          </span>
        </div>
        <div className="flex items-start justify-between gap-3">
          <span className="text-muted-foreground">To</span>
          <span className="flex min-w-0 flex-col items-end gap-0.5">
            <span className="flex items-center justify-end gap-1.5">
              <span className="break-all text-right font-mono text-xs text-foreground">
                {truncateAddress(effectiveRecipient)}
              </span>
              <button
                type="button"
                onClick={handleCopyAddress}
                aria-label="Copy recipient address"
                className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
              >
                {copiedAddress ? (
                  <Check className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </button>
            </span>
            {isDomainRecipient && (
              <span className="text-[11px] text-muted-foreground">{trimmedRecipient}</span>
            )}
          </span>
        </div>
        <div className="flex items-start justify-between gap-3">
          <span className="text-muted-foreground">Amount</span>
          <span className="text-right font-semibold text-foreground">
            {amount} {displaySymbol}
          </span>
        </div>
      </div>

      <div className="mt-4">
        <p className="mb-2 text-sm text-foreground">Your wallet will prompt you to:</p>
        <div className="space-y-3 rounded-xl border border-border modal-inset p-4">
          <div className="flex items-center gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--portal-purple)/0.55)] text-xs font-semibold text-white">
              1
            </span>
            <span className="text-sm text-foreground">Sign the encrypted amount</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--portal-purple)/0.55)] text-xs font-semibold text-white">
              2
            </span>
            <span className="text-sm text-foreground">Confirm transaction request</span>
          </div>
        </div>
      </div>

      {errorMessage && (
        <p className="mt-3 text-sm text-red-500 dark:text-red-400">{errorMessage}</p>
      )}

      <div className="mt-7 flex gap-2">
        <button
          type="button"
          onClick={() => {
            setPhase("input");
            setErrorMessage(null);
          }}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-border py-3 text-sm font-semibold text-foreground transition-all hover:bg-muted/50"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <button
          type="button"
          onClick={handleConfirmSend}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-border modal-cta py-3 text-sm font-semibold text-foreground transition-all hover:border-border hover:opacity-90"
        >
          Send
        </button>
      </div>
    </>
  );

  const renderSendingPhase = () => (
    <div className="py-10 text-center">
      <p className="text-sm font-medium text-foreground">Sending…</p>
      <p className="mt-2 text-xs text-muted-foreground">Approve the prompts in your wallet.</p>
    </div>
  );

  const renderTxLink = () =>
    txHash ? (
      <a
        href={`${explorerBaseUrl}/tx/${txHash}`}
        target="_blank"
        rel="noopener noreferrer"
        className="mb-4 flex items-center justify-center gap-2 text-sm text-primary dark:text-cyan-400 hover:underline"
      >
        View transaction
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    ) : null;

  const renderSuccessPhase = () => (
    <>
      <div className="py-6 text-center">
        <p className="text-sm font-semibold text-foreground">
          {amount} {displaySymbol} has been sent to{" "}
          {isDomainRecipient ? trimmedRecipient : truncateAddress(trimmedRecipient)}.
        </p>
      </div>
      {renderTxLink()}
      <button
        type="button"
        onClick={handleClose}
        className="flex w-full items-center justify-center rounded-xl border border-border modal-cta py-3 text-sm font-semibold text-foreground transition-all hover:border-border hover:opacity-90"
      >
        Done
      </button>
    </>
  );

  const renderErrorPhase = () => (
    <>
      <div className="py-6 text-center">
        <p className="text-sm font-semibold text-foreground">Transfer failed</p>
        {errorMessage && (
          <p className="mt-2 text-xs text-red-500 dark:text-red-400">{errorMessage}</p>
        )}
      </div>
      {renderTxLink()}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => {
            setPhase("review");
            setErrorMessage(null);
            setTxHash(null);
          }}
          className="flex flex-1 items-center justify-center rounded-xl border border-border py-3 text-sm font-semibold text-foreground transition-all hover:bg-muted/50"
        >
          Retry
        </button>
        <button
          type="button"
          onClick={() => {
            setPhase("input");
            setTxHash(null);
          }}
          className="flex flex-1 items-center justify-center rounded-xl border border-border modal-cta py-3 text-sm font-semibold text-foreground transition-all hover:border-border hover:opacity-90"
        >
          Edit
        </button>
      </div>
    </>
  );

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4"
        >
          <div className="absolute inset-0 modal-overlay-blur" onClick={handleClose} />

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-md max-h-[92dvh] overflow-y-auto overscroll-contain rounded-t-2xl sm:rounded-2xl sm:max-h-none sm:overflow-visible border border-border modal-compact p-4 sm:p-5 shadow-2xl"
          >
            <div className="mb-4 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-foreground sm:text-xl">Send Tokens</h2>
                <p className="text-xs text-muted-foreground sm:hidden">
                  Transfer tokens privately to another address.
                </p>
              </div>
              {phase !== "sending" ? (
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:border-border hover:text-foreground sm:h-8 sm:w-8"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : (
                <div className="h-9 w-9 shrink-0 sm:h-8 sm:w-8" aria-hidden />
              )}
            </div>

            {phase === "input" && renderInputPhase()}
            {phase === "review" && renderReviewPhase()}
            {phase === "sending" && renderSendingPhase()}
            {phase === "success" && renderSuccessPhase()}
            {phase === "error" && renderErrorPhase()}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SendModal;
